<template>
  <div class="media-uploader">
    <div class="uploader-content">
      <div class="media-header">
        <div>
          <h1 class="d-flex justify-content-start align-items-center">
            <span v-if="isSelectingDate != null">Select date</span>
            <span v-else-if="isSelectingDistrict != null">Select district</span>
            <span v-else>Upload {{ videoType || "dashcam" }} video</span>
          </h1>
        </div>
        <button
          class="btn-close btn-light"
          @click="closePreview"
          v-if="!isUploading"
          title="Close"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
        >
          &times;
        </button>
      </div>
      <div
        class="media-container"
        v-if="isSelectingDate == null && isSelectingDistrict == null"
      >
        <div class="text-right" style="padding: 15px">
          <button
            class="btn btn-primary btn-sm px-3"
            @click="addVideo"
            v-if="!isUploading && !getUploadStatus"
            title="Click to add video"
            data-toggle="tooltip"
            data-placement="right"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
            Add video
          </button>
        </div>
        <div class="media-list">
          <table class="table table-bordered table-sm small">
            <thead class="table-dark">
              <tr>
                <th
                  class="font-weight-normal text-center border-outline-dark"
                  style="width: 30px"
                >
                  #
                </th>
                <th class="font-weight-normal border-outline-dark">Video</th>
                <th
                  class="font-weight-normal text-center border-outline-dark"
                  style="width: 1%"
                >
                  Size
                </th>
                <th class="font-weight-normal border-outline-dark">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <span>District</span>
                    <button
                      class="btn btn-sm p-0 btn-link text-left ml-auto"
                      @click="openDistrictSelection('all')"
                      v-if="
                        videos.length &&
                        districts.length &&
                        !isUploading &&
                        !getUploadStatus
                      "
                      title="Click to set all districts"
                      data-toggle="tooltip"
                      data-placement="right"
                      @mouseover="helper.showTooltip"
                      @mouseleave="helper.hideTooltip"
                    >
                      <small>Set for all</small>
                    </button>
                  </div>
                </th>
                <th class="border-outline-dark"></th>
              </tr>
            </thead>
            <tbody class="">
              <tr
                v-for="(video, index) in videos"
                :key="`${video.file.name}${index}${renderCounter}`"
              >
                <td class="text-center border-outline-dark">{{ index + 1 }}</td>
                <td class="border-outline-dark">
                  <p class="mb-0 font-weight-medium">{{ video.file.name }}</p>
                  <div
                    class="d-flex justify-content-start align-items-center"
                    v-if="video.srt"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'closed-captioning']"
                      class="mr-2"
                    />
                    <p class="mb-0">
                      {{ video.srt.name }}
                    </p>
                    <button
                      class="btn btn-sm p-0 btn-link text-left mx-2"
                      @click="video.srt = null"
                      v-if="!isUploading && !getUploadStatus"
                      title="Click to remove video"
                      data-toggle="tooltip"
                      data-placement="right"
                      @mouseover="helper.showTooltip"
                      @mouseleave="helper.hideTooltip"
                    >
                      <small>Remove</small>
                    </button>
                  </div>
                  <input
                    ref="srtSelector"
                    type="file"
                    accept=".srt"
                    hidden
                    @change="srtSelected($event, index)"
                  />
                  <button
                    class="btn btn-sm p-0 btn-link text-left"
                    @click="addSrt(index)"
                    v-if="
                      videoType == 'drone' &&
                      !isUploading &&
                      !getUploadStatus &&
                      !video.srt
                    "
                    title="Click to add srt file"
                    data-toggle="tooltip"
                    data-placement="right"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                  >
                    <small>Add srt file</small>
                  </button>
                  <div class="d-flex justify-content-start align-items-center">
                    <font-awesome-icon
                      :icon="['far', 'calendar-alt']"
                      class="mr-2"
                    />
                    <p class="mb-0">
                      {{ formatDate(video.datetime) }}
                    </p>
                  </div>
                </td>
                <td
                  class="align-middle text-nowrap border-outline-dark"
                  style="width: 1%"
                >
                  {{ (video.file.size / 1000000).toFixed(2) }} MB
                </td>
                <td class="align-middle border-outline-dark">
                  <p class="mb-0">
                    {{ video.district ? video.district.name : "" }}
                  </p>
                  <button
                    class="btn btn-sm p-0 btn-link text-left"
                    @click="openDistrictSelection(index)"
                    v-if="
                      districts.length > 0 && !isUploading && !getUploadStatus
                    "
                    title="Click to change district"
                    data-toggle="tooltip"
                    data-placement="right"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                  >
                    <small>Change district</small>
                  </button>
                </td>
                <td
                  class="align-middle text-nowrap border-outline-dark"
                  style="width: 1%"
                >
                  <button
                    v-if="
                      !isUploading &&
                      video.uploaded != true &&
                      video.uploaded != 'error'
                    "
                    class="btn btn-sm btn-link p-0"
                    @click="removeVideo(video.file)"
                    title="Click to remove video"
                    data-toggle="tooltip"
                    data-placement="right"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                  >
                    <small>Remove</small>
                  </button>
                  <div v-else-if="video.uploaded == true">
                    <font-awesome-icon
                      :icon="['fas', 'check-circle']"
                      class="text-success mr-1"
                    />
                    <small class="text-muted">Finished</small>
                  </div>
                  <div v-else-if="video.uploaded == 'error'">
                    <font-awesome-icon
                      :icon="['fas', 'times-circle']"
                      class="text-danger mr-1"
                    />
                    <small class="text-muted">Error</small>
                  </div>
                  <div v-else-if="isUploading && video.uploaded == false">
                    <div class="spinner-border spinner-border-sm mr-1"></div>
                    <small class="text-muted">Waiting</small>
                  </div>
                  <div v-else-if="isUploading && video.uploaded == 'uploading'">
                    <div class="spinner-border spinner-border-sm mr-1"></div>
                    <small class="text-muted">Uploading</small>
                  </div>
                </td>
              </tr>
              <tr v-if="videos.length < 1">
                <td
                  colspan="5"
                  class="text-center font-italic text-muted border-outline-dark"
                >
                  No video selected
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        class="media-container p-3 overflow-auto"
        v-else-if="isSelectingDate != null"
      >
        <v-date-picker
          class="d-block mx-auto"
          :value="videos[isSelectingDate].datetime"
          mode="dateTime"
          trim-weeks
          @input="dateInputChange"
        />
      </div>
      <div
        class="media-container p-3 overflow-auto"
        v-else-if="isSelectingDistrict != null"
      >
        <ul class="list-group list-group-flush">
          <li
            class="list-group-item bg-white shadow-sm clickable text-dark"
            v-for="district in districts"
            :key="district._id"
            @click="districtSearchModel = district"
          >
            {{ district.name }}
            <font-awesome-icon
              :icon="['fas', 'check']"
              class="float-right text-dark"
              v-if="
                districtSearchModel && districtSearchModel._id == district._id
              "
            />
          </li>
          <li
            class="list-group-item bg-dark font-italic text-muted text-center small"
            v-if="districts.length < 1"
          >
            No district found
          </li>
        </ul>
      </div>
      <div class="media-footer border-outline-dark-top">
        <input
          ref="fileSelector"
          type="file"
          :accept="acceptedFileType"
          @change="mediaSelected"
          multiple
          hidden
        />

        <button
          class="btn btn-outline-secondary btn-sm"
          @click="closePreview"
          v-if="!isUploading && !getUploadStatus"
          title="Click to cancel"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
        >
          Cancel
        </button>
        <p class="small mb-0" v-else-if="timeTaken && getUploadStatus">
          Time taken
          <span class="badge badge-secondary ml-1">
            {{ timeTaken.toFixed(2) }} minutes
          </span>
        </p>
        <button
          class="btn btn-success btn-sm px-2 d-flex justify-content-between align-items-center ml-auto"
          style="width: 200px"
          disabled
          v-if="isUploading"
          title="Click to upload"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
        >
          <div>
            <div class="spinner-border spinner-border-sm mr-1"></div>
            Uploading
          </div>
          <p class="mb-0">
            {{ videos.filter((p) => p.uploaded).length }} /
            {{ videos.length }}
          </p>
        </button>
        <template v-else-if="getUploadStatus">
          <button
            class="btn btn-warning btn-sm px-3 ml-auto mr-3"
            @click="retryUpload()"
            v-if="videos.filter((p) => p.uploaded == 'error').length > 0"
            title="Click to retry"
            data-toggle="tooltip"
            data-placement="right"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
          >
            Retry
            {{ videos.filter((p) => p.uploaded == "error").length }} video(s)
          </button>
          <button
            class="btn btn-success btn-sm px-3"
            @click="closePreview"
            title="close"
            data-toggle="tooltip"
            data-placement="right"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
          >
            Finish
          </button>
        </template>
        <button
          class="btn btn-success btn-sm"
          :disabled="videos.length < 1"
          v-else-if="isSelectingDate == null && isSelectingDistrict == null"
          @click="uploadVideos"
          title="Click to upload"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
        >
          Upload
          <span v-if="videos.length > 0">{{ videos.length }} videos</span>
        </button>
        <button
          class="btn btn-primary btn-sm"
          v-else-if="isSelectingDate != null"
          @click="dateSelected"
          :disabled="!dateSelectModel"
          title="Select date"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
        >
          Select date
        </button>
        <button
          title="Select district"
          data-toggle="tooltip"
          data-placement="right"
          @mouseover="helper.showTooltip"
          @mouseleave="helper.hideTooltip"
          class="btn btn-primary btn-sm"
          v-else-if="isSelectingDistrict != null"
          @click="districtSelected"
          :disabled="!districtSearchModel"
        >
          Select district
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Moment from "moment";
import API from "@/api";
import Helper from "@/helper";

export default {
  name: "media-uploader",
  props: ["districts", "districtFrom", "videoType"],
  data() {
    return {
      user: {},
      acceptedFileType: "video/*",
      isUploading: false,
      isSelectingDate: null,
      isSelectingDistrict: null,
      videos: [],
      districtSearchModel: null,
      dateSelectModel: null,
      renderCounter: 0,
      timeTaken: null,
      helper: Helper,
    };
  },
  computed: {
    getUploadStatus() {
      let result =
        this.videos.length > 0 &&
        this.videos.every((p) => {
          return p.uploaded == true || p.uploaded == "error";
        });

      return result;
    },
  },
  methods: {
    async retryUpload() {
      let failedVideos = this.videos.filter(
        (video) => video.uploaded === "error"
      );

      failedVideos.forEach((video) => {
        video.uploaded = false;
      });

      await this.uploadVideos();
    },
    uploadSrt(video) {
      if (this.videoType == "drone" && video.srt) {
        return new Promise((resolve) => {
          let srtFormData = new FormData();

          srtFormData.append("files", video.srt, video.srt.name);

          API.postForm("upload", srtFormData, 60 * 60000)
            .then((retVal) => {
              resolve({ success: true, data: retVal.data[0] });
            })
            .catch((err) => {
              reject({ success: false, data: err });
            });
        });
      }
    },
    async doUpload(video) {
      return new Promise(async (resolve, reject) => {
        this.updateVideoMeta(video.id, "uploading");

        let formData = new FormData();

        let data = {
          datetime: video.datetime,
          isProcessed: false,
        };

        if (this.user?.contractor) {
          data.contractor = this.user.contractor.id;
        }

        if (video.district) {
          data.district = video.district._id;
        }

        if (this.videoType == "drone" && video.srt) {
          const srtData = await this.uploadSrt(video);

          if (srtData.success) {
            data.srt = srtData.data._id;
          } else {
            reject("SRT error");
          }
        }

        formData.append("files", video.file, video.file.name);

        API.postForm("upload", formData, 60 * 60000)
          .then(async (retVal) => {
            data.video = retVal.data[0]._id;

            await API.post(
              this.videoType == "drone" ? "drone-videos" : "dashcamvideos",
              data
            );

            this.updateVideoMeta(video.id, true);

            this.$emit("uploaded", retVal.data);

            resolve(retVal.data);
          })
          .catch((err) => {
            console.error(err);

            this.updateVideoMeta(video.id, "error");

            reject(err);
          });
      });
    },
    async uploadVideos() {
      const videosWithDistrict = this.videos.filter(
        (video) => video.district
      ).length;

      if (videosWithDistrict !== this.videos.length) {
        alert("Please select the district for every video");
        return;
      }

      let startTime = Moment();

      this.isUploading = true;

      this.videos = this.videos.map((p, index) => {
        p.id = index;

        return p;
      });

      for (const [index, video] of this.videos
        .filter((video) => video.uploaded === false)
        .entries()) {
        try {
          await this.doUpload(video, index);
        } catch (error) {
          continue;
        }
      }

      let endTime = Moment();

      this.timeTaken = endTime.diff(startTime, "minutes", true);

      this.isUploading = false;
    },
    updateVideoMeta(index, text) {
      this.videos[index].uploaded = text;
      this.renderCounter += 1;
    },
    dateSelected() {
      this.videos[this.isSelectingDate].datetime = this.dateSelectModel;

      this.isSelectingDate = null;
      this.dateSelectModel = null;
    },
    dateInputChange(e) {
      this.dateSelectModel = e;
    },
    districtSelected() {
      if (this.isSelectingDistrict === "all") {
        this.videos.forEach((video) => {
          video.district = this.districtSearchModel;
        });
      } else {
        this.videos[this.isSelectingDistrict].district =
          this.districtSearchModel;
      }

      this.isSelectingDistrict = null;
      this.districtSearchModel = null;
    },
    formatDate(date) {
      if (!date) {
        return "";
      } else {
        return Moment(date).format("D MMM YYYY, h:mm A");
      }
    },
    openDateSelection(index) {
      this.isSelectingDate = index;
    },
    openDistrictSelection(index) {
      this.isSelectingDistrict = index;

      this.districtSearchModel = this.videos[0].district;
    },
    removeVideo(video) {
      this.videos = this.videos.filter((v) => v.file != video);
    },
    mediaSelected(event) {
      if (event.target.files.length < 1) return;

      let files = event.target.files;

      const srtFiles = [];

      Array.from(files).forEach((file) => {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        const fileName = file.name.split(".").shift().toLowerCase();

        if (fileExtension !== "srt") {
          const newVideo = {
            road: null,
            datetime: new Date(),
            file: file,
            srt: null,
            uploaded: false,
            district:
              this.districtFrom == "user" && this.districts.length > 0
                ? this.districts[0]
                : null,
          };

          if (this.videoType == "drone") {
            newVideo.srt = null;
          }

          this.videos.push(newVideo);
        } else {
          srtFiles.push({
            name: fileName,
            file: file,
          });
        }
      });

      srtFiles.forEach((srt) => {
        const _video = this.videos.find(
          (_vid) =>
            _vid.file.name
              .substring(0, _vid.file.name.lastIndexOf("."))
              .toLowerCase() == srt.name
        );

        if (_video) {
          _video.srt = srt.file;
        }
      });
    },
    addVideo() {
      this.acceptedFileType =
        this.videoType == "drone" ? "video/*, .srt" : "video/*";

      this.$nextTick(() => {
        this.$refs.fileSelector?.click();
      });
    },
    addSrt(index) {
      this.$refs.srtSelector[index]?.click();
    },
    srtSelected(event, index) {
      if (event.target.files.length < 1) return;

      let file = event.target.files[0];

      this.videos[index].srt = file;
    },
    closePreview() {
      if (this.isSelectingDate != null || this.isSelectingDistrict != null) {
        this.isSelectingDate = null;
        this.dateSelectModel = null;

        this.isSelectingDistrict = null;
        this.districtSearchModel = null;
      } else {
        this.$emit("close");
      }
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user") || {});
  },
};
</script>